/**
 * PATH: packages/ui/src/kanban/components/board-view/useBoardUnseenEmails.ts
 * FILE_ID: UI-KANBAN-HOOK-ARC5-UNSEEN
 * PURPOSE: Board-scoped fetch of the CALLER's per-card unseen-email counts —
 *          the data behind the "Focus: unseen" filter toggle and each card's
 *          "N unseen" chip.
 * WHY: ARC-5 U18 — a PM must see which board emails they have never opened
 *      anywhere and don't have in their own mailbox. The server owns the
 *      definition (seen-state ∪ own-mailbox exclusion, see BE
 *      email_kanban/services/seen_state.py); the FE fetches one aggregate
 *      per board (mirrors useBoardRelatedCounts, the established pattern).
 * FLOW:
 *   board -> getBoardUnseenEmails(boardId) -> { counts, total }
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { KanbanApi } from '../../api'

/** Query key builder — exported so mark-seen callers can invalidate it. */
export function boardUnseenQueryKey(boardId?: string) {
  return boardId
    ? (['kanban', 'board-unseen-emails', boardId] as const)
    : (['kanban', 'board-unseen-emails'] as const)
}

export interface UseBoardUnseenEmailsArgs {
  api: KanbanApi
  boardId: string | undefined
  /** Gate the whole thing off when the board view isn't active. */
  enabled?: boolean
}

export interface BoardUnseenState {
  /** cardId → number of linked emails the caller hasn't seen. Missing = 0. */
  counts: Record<string, number>
  /** Total unseen emails across the board (the focus toggle's badge). */
  total: number
  /** Whether this board view renders unseen-email decorations. */
  isRequired: boolean
  /** True only after the required aggregate finishes without an error. */
  isSettledSuccessfully: boolean
  isError: boolean
}

/**
 * I→O: board → the caller's unseen map, total, and completion state. Older
 * hosts have no unseen-email decoration, so they are not required to settle.
 */
export function useBoardUnseenEmails({
  api,
  boardId,
  enabled = true,
}: UseBoardUnseenEmailsArgs): BoardUnseenState {
  const canFetch = typeof api.getBoardUnseenEmails === 'function'
  const active = enabled && canFetch && Boolean(boardId)

  const q = useQuery({
    queryKey: boardUnseenQueryKey(boardId),
    queryFn: () => api.getBoardUnseenEmails!(boardId as string),
    enabled: active,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  return useMemo(() => {
    return {
      counts: active ? q.data?.unseen_by_card ?? {} : {},
      total: active ? q.data?.total_unseen_emails ?? 0 : 0,
      isRequired: active,
      isSettledSuccessfully: active && q.isSuccess && !q.isFetching && !q.isError,
      isError: active && q.isError,
    }
  }, [active, q.data, q.isError, q.isFetching, q.isSuccess])
}
